import { InformationPad } from '../../../components/shared';
import { SUB_LOCATION_DETAIL_LABEL } from '../../../constants/accordionFields';
import {
  getDateFormat,
  getTimeFormatForStaticView,
  mergedTwoPhoneNumber,
  getUsersMergeString,
  isArrayWithLength,
  getTruncatedDateFormat,
  checkNAOrReturnPercentValue,
  formatTaxPercentValue,
} from '../../../utils/common-methods';
import YesNoIndicator from '../../../components/shared/YesNoIndicator';
import { isArcadeOrGameRoomType } from '../../../hooks';
import { getFeedSourceDisplayText, getCreditCashDisplayText } from '../LocationDetails/SubLocationDetailAccordion';
import { useGetSubLocationTypesQuery } from '../../../services/Sublocation/SublocationService';
import { Tooltip, Chip } from '@mui/material';

const SubLocationDetailAccordion = (props) => {
  const { subLocationInfo } = props;

  const { currentData: locationTypesData } = useGetSubLocationTypesQuery();
  const leaseCCFeeRate =
    subLocationInfo?.leaseCreditCardFeeRate != null ? Number(subLocationInfo.leaseCreditCardFeeRate) : undefined;

  let productTypeStr = '';
  if (isArrayWithLength(subLocationInfo?.productCategories)) {
    let productNames = subLocationInfo?.productCategories?.map((item) => item?.name || '');
    productTypeStr = productNames?.join(', ');
  }
  const fields = [
    {
      label: SUB_LOCATION_DETAIL_LABEL.LOCATION_TYPE,
      value: subLocationInfo?.sublocationType?.name,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.PHONE,
      value: mergedTwoPhoneNumber(subLocationInfo?.phone1, subLocationInfo?.phone2),
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.OPEN_HOURS,
      value:
        subLocationInfo?.openHours && subLocationInfo?.closeHours
          ? `${getTimeFormatForStaticView(subLocationInfo?.openHours)} - ${getTimeFormatForStaticView(
              subLocationInfo?.closeHours,
            )}`
          : null,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.PLAY_TYPE,
      value: subLocationInfo?.playType,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.LOCATION,
      value: subLocationInfo?.location?.locationName,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.MANAGER,
      value: getUsersMergeString(subLocationInfo?.users),
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.START_DATE,
      value: getDateFormat(subLocationInfo?.startDate),
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.END_DATE,
      value: getTruncatedDateFormat(subLocationInfo?.endDate),
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.UNINSTALL_DATE,
      value: subLocationInfo?.uninstallDate ? getDateFormat(subLocationInfo?.uninstallDate) : null,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.SPACE_IDENTIFIER,
      value: subLocationInfo?.spaceIdentifier,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.ADDRESS1,
      value: subLocationInfo?.address1,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.ADDRESS2,
      value: subLocationInfo?.address2,
    },
    { label: SUB_LOCATION_DETAIL_LABEL.ZIPCODE, value: subLocationInfo?.code },
    {
      label: SUB_LOCATION_DETAIL_LABEL.COUNTRY,
      value: subLocationInfo?.location?.country?.name,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.STATE,
      value: subLocationInfo?.location?.state?.name,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.CITY,
      value: subLocationInfo?.location?.city?.name,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.PRODUCT_TYPE,
      value: productTypeStr || 'N/A',
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.VENDOR_CODE,
      value: subLocationInfo?.vendor?.vendorCode,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.VENDOR_NAME,
      value: subLocationInfo?.vendor?.customerName,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.STATE_TAX,
      value: formatTaxPercentValue(subLocationInfo?.location?.state?.stateTaxHistories?.[0]?.stateTax),
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.AREA_TAX_LOCATION,
      value: subLocationInfo?.areaTaxLocations?.areaTaxLocationName,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.AREA_TAX,
      value: formatTaxPercentValue(subLocationInfo?.areaTaxHistories?.[0]?.areaTax),
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.MISC_TAX,
      value: checkNAOrReturnPercentValue(subLocationInfo?.miscTax),
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.CC_FEE,
      value: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {checkNAOrReturnPercentValue(subLocationInfo?.creditCardFeeRate ?? leaseCCFeeRate)}
          <Tooltip
            title={
              subLocationInfo?.creditCardFeeRate != null ? 'overridden from Sublocation edit' : 'Lease Default CC Fee'
            }
            arrow
          >
            <Chip
              label={subLocationInfo?.creditCardFeeRate != null ? 'Custom CC fee' : 'Lease default CC fee'}
              size="small"
              color={subLocationInfo?.creditCardFeeRate != null ? 'primary' : 'default'}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          </Tooltip>
        </span>
      ),
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.PENNY_COST,
      value: checkNAOrReturnPercentValue(subLocationInfo?.pennyCostPercentage),
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.TAX_EXEMPT_FLAG,
      value: <YesNoIndicator value={subLocationInfo?.taxExemptFlag === true} />,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.LOC_PAYS_TAX,
      value: <YesNoIndicator value={subLocationInfo?.locPaysTaxFlag === true} />,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.CALC_RENT,
      value: <YesNoIndicator value={subLocationInfo?.calculateRent === true} />,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.CREDIT_CARD_IN_COLLECTION,
      value: <YesNoIndicator value={subLocationInfo?.isCreditCardInCollection === true} />,
    },
    { label: SUB_LOCATION_DETAIL_LABEL.FEED_SOURCE, value: getFeedSourceDisplayText(subLocationInfo?.feedSource) },
    {
      label: SUB_LOCATION_DETAIL_LABEL.CREDIT_CASH_CONTROL,
      value: getCreditCashDisplayText(subLocationInfo?.creditCashControl),
    },
    ...(isArcadeOrGameRoomType(subLocationInfo?.sublocationType?.id, locationTypesData)
      ? [{ label: SUB_LOCATION_DETAIL_LABEL.POSTING_LOCATION, value: subLocationInfo?.postingLocation }]
      : []),
  ];
  return <InformationPad data={fields} />;
};

export default SubLocationDetailAccordion;
